import React from 'react'
import { CheckCircle2, Clock, MapPin, Copy } from 'lucide-react'
import { toast } from 'sonner'

interface TimelineEvent {
  status: string
  message: string
  created_at: string
}

export interface OrderTrackingData {
  order_number: string
  status: string
  customer_name: string
  tracking_id: string
  shipping_city: string
  shipping_state: string
  total: number
  created_at: string
  timeline: TimelineEvent[]
}

const steps = [
  { key: 'pending', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'processing', label: 'Packing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
]

function getStepIndex(status: string) {
  const idx = steps.findIndex((s) => s.key === status)
  return idx >= 0 ? idx : 0
}

export function OrderTimeline({ data }: { data: OrderTrackingData }) {
  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)

  const handleCopyTrackingId = (trackingId: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(trackingId)
        .then(() => toast.success('Tracking ID copied to clipboard!'))
        .catch(() => fallbackCopy(trackingId))
    } else {
      fallbackCopy(trackingId)
    }
  }

  const fallbackCopy = (text: string) => {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      toast.success('Tracking ID copied to clipboard!')
    } catch {
      toast.error('Failed to copy tracking ID')
    }
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="rounded-xl border border-[var(--border)] p-4 space-y-2 text-xs bg-[var(--bg-surface-2)]">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <span className="font-mono font-bold text-[var(--gold-main)]">{data.order_number}</span>
          <span className={`badge badge-${data.status}`}>{data.status}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)]">Customer</span>
          <span>{data.customer_name}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[var(--text-muted)]">Tracking ID</span>
          <span className="font-mono text-[var(--gold-main)] font-bold flex items-center gap-1.5">
            {data.tracking_id}
            <button
              type="button"
              onClick={() => handleCopyTrackingId(data.tracking_id)}
              className="p-1 hover:bg-[var(--gold-main)]/10 rounded transition"
              title="Copy Tracking ID"
            >
              <Copy className="w-3.5 h-3.5 text-[var(--gold-main)]" />
            </button>
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)]">Destination</span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3 text-[var(--gold-main)]" />
            {data.shipping_city}, {data.shipping_state}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)]">Total</span>
          <span className="font-bold text-[var(--gold-main)]">{formatCurrency(data.total)}</span>
        </div>
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="flex items-center justify-between min-w-[320px] relative">
          <div className="absolute top-3 left-0 right-0 h-0.5 bg-[var(--border)]" />
          <div
            className="absolute top-3 left-0 h-0.5 bg-[var(--gold-main)] transition-all"
            style={{ width: `${(getStepIndex(data.status) / (steps.length - 1)) * 100}%` }}
          />
          {steps.map((stepItem, idx) => {
            const currentIdx = getStepIndex(data.status)
            const isCompleted = idx <= currentIdx
            return (
              <div key={stepItem.key} className="relative z-10 flex flex-col items-center flex-1">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${
                    isCompleted
                      ? 'bg-[var(--gold-main)] text-[var(--gold-contrast)]'
                      : 'bg-[var(--bg-surface-3)] text-[var(--text-faint)] border border-[var(--border)]'
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx + 1}
                </div>
                <span className="text-[9px] mt-1 text-center text-[var(--text-muted)]">{stepItem.label}</span>
              </div>
            )
          })}
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)]">Activity log</h2>
        {data.timeline?.map((ev, i) => (
          <div
            key={i}
            className="p-3 rounded-lg border border-[var(--border)] text-xs flex gap-2 bg-[var(--bg-surface-2)]"
          >
            <Clock className="w-3.5 h-3.5 text-[var(--gold-main)] shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{ev.message}</p>
              <p className="text-[10px] text-[var(--text-faint)] mt-0.5">
                {new Date(ev.created_at).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
